// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { areAddressesTheSame } from "@insite/client-framework/Common/CustomerSelectors";
import {
    makeHandlerChainAwaitable,
    replaceInChain
} from "@insite/client-framework/HandlerCreator";
import { createShipTo, updateBillTo, updateShipTo } from "@insite/client-framework/Services/CustomersService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    AddOrUpdateShipTo,
    chain,
    UpdateBillTo
} from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SaveShipping";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { CustomAppState } from "@turnerBlueprint/App/Store/Reducers";
import { xIsAuthenticated } from "@turnerBlueprint/App/Store/Selectors/GeneralSelectors";

export const XngUpdateBillTo: typeof UpdateBillTo = async props => {
    const {
        getState,
        apiParameter: { cart },
    } = props;

    const {
        context: {
            session: { fulfillmentMethod },
        },
        pages: {
            onePageCheckout: { shippingAddressFormState, billingAddressFormState, isBillingAddressTheSameAsShipping },
        },
        xCheckout,
    } = getState() as CustomAppState;

    const isAuthenticated = xIsAuthenticated(getState());

    if (!shippingAddressFormState || !billingAddressFormState) {
        return;
    }

    const { address: localModifiedShipTo } = shippingAddressFormState;
    const localModifiedBillTo = { ...billingAddressFormState.address };
    // XNG-Changes: add !xCheckout.useOneTimeAddress
    if (
        (isBillingAddressTheSameAsShipping || (!isAuthenticated && xCheckout.useBillingAddress)) &&
        fulfillmentMethod === FulfillmentMethod.Ship
    ) {
        updateBillToWithShipToFields(localModifiedBillTo, localModifiedShipTo);
    }

    // XNG-Changes
    if (!isAuthenticated && !isBillingAddressTheSameAsShipping && !xCheckout.useBillingAddress) {
        localModifiedBillTo.firstName = xCheckout.ccBillingFirstName;
        localModifiedBillTo.lastName = xCheckout.ccBillingLastName;
        localModifiedBillTo.address1 = xCheckout.ccBillingAddress1;
        localModifiedBillTo.city = xCheckout.ccBillingCity;
        localModifiedBillTo.country = xCheckout.ccBillingCountry;
        localModifiedBillTo.state = xCheckout.ccBillingState;
        localModifiedBillTo.postalCode = xCheckout.ccBillingPostalCode;
        localModifiedBillTo.phone = xCheckout.ccBillingPhone;
        localModifiedBillTo.companyName = shippingAddressFormState.address.companyName;
        localModifiedBillTo.email = shippingAddressFormState.address.email;
    }

    const billTo = getBillToState(getState(), cart.billToId).value;
    if (!billTo) {
        throw new Error("There was no billTo loaded for the current cart");
    }

    if (areAddressesTheSame(billTo, localModifiedBillTo)) {
        props.updatedBillTo = billTo;
    } else {
        const updatedBillTo = await updateBillTo({ billTo: localModifiedBillTo, isEdit: false });
        props.updatedBillTo = { ...updatedBillTo, validation: billTo.validation };
        props.dispatch({
            type: "Pages/OnePageCheckout/SetBillingAddress",
            address: props.updatedBillTo,
        });
    }

    props.apiParameter.cart.billToId = props.updatedBillTo.id;
};

export const XngAddOrUpdateShipTo: typeof AddOrUpdateShipTo = async props => {
    const { dispatch, getState, cart } = props;
    const state = getState() as CustomAppState;
    const {
        context: {
            session: { fulfillmentMethod },
        },
        pages: {
            onePageCheckout: { shippingAddressFormState, isBillingAddressTheSameAsShipping },
        },
        // XNG-Changes
        xCheckout: { useOneTimeAddress },
    } = state;
    if (!shippingAddressFormState || fulfillmentMethod !== FulfillmentMethod.Ship) {
        return;
    }

    const shipTo = getShipToState(state, cart.shipToId).value;
    const billTo = getBillToState(state, cart.billToId).value;
    if (!shipTo || !billTo) {
        throw new Error("There was no shipTo or billTo loaded for the current cart");
    }

    const { address: localModifiedShipTo } = shippingAddressFormState;
    if (isBillingAddressTheSameAsShipping && cart.shipToId !== cart.billToId && cart.billToId) {
        props.apiParameter.cart.shipToId = cart.billToId;
        const { shipToId } = props.apiParameter.cart;
        await makeHandlerChainAwaitable(loadShipTo)({ shipToId, billToId: cart.billToId, expand: ["validation"] })(
            dispatch,
            getState,
        );
        const shipToState = getShipToState(getState(), shipToId);
        shipToState.value &&
            props.dispatch({
                type: "Pages/OnePageCheckout/SetShippingAddress",
                address: shipToState.value,
            });
        return;
    }

    let updatedShipTo;
    // XNG-Changes : add useOneTimeAddress
    if ((!isBillingAddressTheSameAsShipping && cart.shipToId === cart.billToId) || useOneTimeAddress) {
        updatedShipTo = await createShipTo({ shipTo: localModifiedShipTo });
    } else if (!areAddressesTheSame(shipTo, localModifiedShipTo)) {
        updatedShipTo = await updateShipTo({ shipTo: localModifiedShipTo, billToId: billTo.id });
    }

    if (updatedShipTo) {
        props.updatedShipTo = { ...updatedShipTo, validation: shipTo.validation };
        dispatch(loadShipTo({ shipToId: props.updatedShipTo.id, billToId: billTo.id }));
        props.dispatch({
            type: "Pages/OnePageCheckout/SetShippingAddress",
            address: props.updatedShipTo,
        });
        props.apiParameter.cart.shipToId = props.updatedShipTo.id;
    }
};



const updateBillToWithShipToFields = (billTo: BillToModel, shipTo: ShipToModel) => {
    billTo.firstName = shipTo.firstName;
    billTo.lastName = shipTo.lastName;
    billTo.attention = shipTo.attention;
    billTo.companyName = shipTo.companyName;
    billTo.address1 = shipTo.address1;
    billTo.address2 = shipTo.address2;
    billTo.address3 = shipTo.address3;
    billTo.address4 = shipTo.address4;
    billTo.country = shipTo.country;
    billTo.city = shipTo.city;
    billTo.state = shipTo.state;
    billTo.postalCode = shipTo.postalCode;
    billTo.phone = shipTo.phone;
    billTo.email = shipTo.email;
    billTo.fax = shipTo.fax;
    billTo.contactFullName = shipTo.contactFullName;
    billTo.fullAddress = shipTo.fullAddress;
};

replaceInChain(chain, UpdateBillTo, XngUpdateBillTo);
replaceInChain(chain, AddOrUpdateShipTo, XngAddOrUpdateShipTo);
